import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import PATHS from '@/common/paths';
import { DebitCardPayload } from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/DebitCart/hooks';
import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import { EmployeePayload } from '@/modules/user/user.types';

import { formatPaymentMethod } from './useAddPaymentMethod.query';

const ADDRESS_TYPE = 'HOME';

export interface DebitCardPaymentMethodRequestPayload {
  token_id?: string;
  name_on_card?: string;
  encrypted_card_number?: string;
  encrypted_expiration_month?: string;
  encrypted_expiration_year?: string;
  card_expiration_date?: string;
  card_last4?: string;
  card_type?: string;
  address_type?: string;
  street_address?: string;
  locality?: string;
  region?: string;
  postal_code?: string;
  country?: string;
}

export interface DebitCardPaymentMethodRequest {
  tokenId?: string;
  paymentType?: string;
  debitCard?: DebitCardPayload;
  personalInfo?: EmployeePayload;
  cardNumber?: string;
  expiryDateMonth?: string;
  expiryDateYear?: string;
  fullExpDate?: string;
}
const formatDataToCreateDebitCardPayload = ({
  tokenId,
  debitCard,
  personalInfo,
  cardNumber,
  expiryDateMonth,
  expiryDateYear,
  fullExpDate,
}: DebitCardPaymentMethodRequest): DebitCardPaymentMethodRequestPayload => ({
  token_id: tokenId,
  name_on_card: debitCard?.nameOnCard,
  encrypted_card_number: cardNumber,
  encrypted_expiration_month: expiryDateMonth,
  encrypted_expiration_year: expiryDateYear,
  card_expiration_date: fullExpDate,
  card_last4: debitCard?.cardNumber?.slice(-4),
  card_type: debitCard?.cardType,
  address_type: ADDRESS_TYPE,
  street_address: `${personalInfo?.physical_address?.line1 || ''} ${
    personalInfo?.physical_address?.line2 || ''
  }`.trim(),
  locality: personalInfo?.physical_address?.city,
  region: personalInfo?.physical_address?.state,
  postal_code: personalInfo?.physical_address?.zipcode,
  country: personalInfo?.physical_address?.country_code,
});

const useCreateDebitCardPaymentMethodQuery = (
  onSuccess?: (paymentMethodId?: string) => void,
  onError?: (errorText?: string) => void
) => {
  const queryClient = useQueryClient();
  const handleAddJustCreatedPaymentMethod = useStore((state) => state.handleAddJustCreatedPaymentMethod);

  const [isLoading, setLoading] = useState(false);
  const { mutateAsync, isSuccess } = useMutation(
    (value: DebitCardPaymentMethodRequest) =>
      api.post(PATHS.CREATE_DEBIT_CARD_PAYMENT_METHOD, formatDataToCreateDebitCardPayload(value)),
    {
      onSuccess: (response) => {
        const paymentMethodId = response?.data?.id ? toString(response?.data?.id) : undefined;
        queryClient.invalidateQueries({
          queryKey: [PATHS.PAYMENT_METHODS, { refetchInactive: true }],
        });
        setLoading(false);

        if (onSuccess) {
          onSuccess(paymentMethodId);
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (data?: any) => {
        setLoading(false);
        if (onError) onError(data?.response?.data?.elevate_error_message);
      },
    }
  );
  const createDebitCardPaymentMethod = useCallback(
    async (value: DebitCardPaymentMethodRequest) => {
      setLoading(true);
      const { data } = await mutateAsync(value);
      handleAddJustCreatedPaymentMethod(formatPaymentMethod(data));
      return data;
    },
    [handleAddJustCreatedPaymentMethod, mutateAsync]
  );

  return { isLoading, createDebitCardPaymentMethod, isSuccess };
};

export default useCreateDebitCardPaymentMethodQuery;
